import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

const SIDEBAR_WIDTH = 232;

const NAV: { to: string; label: string; Icon: SvgIconComponent }[] = [
  { to: '/', label: 'Dashboard', Icon: DashboardOutlined },
  { to: '/pedidos', label: 'Pedidos', Icon: ReceiptLongOutlined },
  { to: '/plataformas', label: 'Plataformas', Icon: StorefrontOutlined },
  { to: '/custos', label: 'Custos', Icon: PaidOutlined },
  { to: '/operacoes', label: 'Operações', Icon: TuneOutlined },
];

function ThemeToggle() {
  const { mode, systemMode, setMode } = useColorScheme();
  const current = mode === 'system' ? systemMode : mode;
  const next = current === 'dark' ? 'light' : 'dark';

  return (
    <Tooltip title={next === 'dark' ? 'Tema escuro' : 'Tema claro'}>
      <IconButton size="small" onClick={() => setMode(next)} aria-label="Alternar tema">
        {next === 'dark' ? <DarkModeOutlined fontSize="small" /> : <LightModeOutlined fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  const { admin, logout } = useAuth();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, pt: 0.5 }}>
        <Box
          component="img"
          src="/favicon.svg"
          alt=""
          sx={{ width: 28, height: 28, borderRadius: 1 }}
        />
        <Box>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
            Recompensas
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Painel administrativo
          </Typography>
        </Box>
      </Box>

      <Box component="nav" sx={{ display: 'grid', gap: 0.5 }}>
        {NAV.map(({ to, label, Icon }) => (
          <Box
            key={to}
            component={NavLink}
            to={to}
            end={to === '/'}
            {...(onNavigate === undefined ? {} : { onClick: onNavigate })}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              color: 'text.secondary',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
              '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              '&.active': { bgcolor: 'action.selected', color: 'text.primary', fontWeight: 600 },
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
            {label}
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {admin?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {admin?.email}
          </Typography>
        </Box>
        <ThemeToggle />
        <Tooltip title="Sair">
          <IconButton size="small" onClick={logout} aria-label="Sair">
            <LogoutRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function Layout() {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          height: '100vh',
          bgcolor: 'background.paper',
        }}
      >
        <Navigation />
      </Box>

      <Drawer open={open} onClose={() => setOpen(false)} sx={{ display: { md: 'none' } }}>
        <Box sx={{ width: SIDEBAR_WIDTH, height: '100%' }}>
          <Navigation onNavigate={() => setOpen(false)} />
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <IconButton onClick={() => setOpen(true)} aria-label="Abrir menu">
            <MenuRounded />
          </IconButton>
          <Typography variant="subtitle2">Recompensas</Typography>
        </Box>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, md: 4 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
